import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthContext";
import { useCategories } from "../../contexts/CategoryContext";
import { useSubCategories } from "../../contexts/SubCategoryContext";
import { useChat } from "../../contexts/ChatContext";
import { getCategoryIcon } from "../../utils/getCategoryIcon";
import SubCategorySelectionModal from "./SubCategorySelectionModal";
import toast from "react-hot-toast";
import { localizedField, pickLocalizedFields } from "../../utils/localizedField";
import { useAppLanguage } from "../../utils/appLanguage";

const ASTRIA_COUNTRY_CATEGORIES = [
  "Astria US",
  "Astria Spanish",
  "Astria Spanish V2",
  "Astria Japan",
  "Astria Korea",
  "Astria Korea V2",
  "Astria Korea V3",
  "Astria Korea Talk",
  "Astria Korea Hybrid",
  "Astria Japan V2",
  "Astria Japan Talk",
  "Astria Japan V3",
  "Astria Japan Hybrid",
  "Astria Brazil",
  "Astria Brazil V2",
  "social compatibility",
  "Astria Mexico",
  "Astria Mexico V2",
  "Astria India",
  "Astria India V2",
  "Astria India V3",
  "Astria PSM",
  "Astria Philippines",
  "Astria Philippines V2",
  "Astria Singapore",
  "Astria Singapore V2",
  "Astria Singapore V3",
  "Astria Malaysia",
  "Astria Malaysia V2",
  "Astria Malaysia V3",
  "Astria Indonesia",
  "Astria Indonesia V2",
  "Astria Indonesia V3",
  "Astria Indonesia Talk",
  "Astria Vietnam V2",
  "Astria Vietnam",
  "Astria UK",
  "Astria UK V2",
  "Astria Canada",
  "Astria Canada V2",
  "Astria Canada V3",
  "Astria GCC",
  "Astria GCC V2",
  "Astria Italy",
  "Astria Germany",
  "Astria China",
  "Astria Russia",
  "Astria France",
];

const REGION_TO_ASTRIA: Record<string, string[]> = {
  indonesia: [
    "Astria Indonesia",
    "Astria Indonesia V2",
    "Astria Indonesia V3",
    "Astria Indonesia Talk",
  ],
  vietnam: ["Astria Vietnam V2", "Astria Vietnam"],
  canada: ["Astria Canada", "Astria Canada V2", "Astria Canada V3"],
  uk: ["Astria UK", "Astria UK V2"],
  gcc: ["Astria GCC", "Astria GCC V2"],
  malaysia: ["Astria Malaysia", "Astria Malaysia V2", "Astria Malaysia V3"],
  singapore: ["Astria Singapore", "Astria Singapore V2", "Astria Singapore V3"],
  philippines: ["Astria Philippines", "Astria Philippines V2"],
  brazil: ["Astria Brazil", "Astria Brazil V2", "social compatibility"],
  mexico: ["Astria Mexico", "Astria Mexico V2"],
  korea: [
    "Astria Korea",
    "Astria Korea V2",
    "Astria Korea V3",
    "Astria Korea Talk",
    "Astria Korea Hybrid",
  ],
  india: ["Astria India", "Astria India V2", "Astria India V3"],
  japan: [
    "Astria Japan",
    "Astria Japan V2",
    "Astria Japan Talk",
    "Astria Japan V3",
    "Astria Japan Hybrid",
  ],
  spanish: ["Astria Spanish", "Astria Spanish V2"],
  us: ["Astria US"],
  italy: ["Astria Italy"],
  germany: ["Astria Germany"],
  china: ["Astria China"],
  russia: ["Astria Russia"],
  france: ["Astria France"],
};

const HEALJAI_ONLY_CATEGORIES = new Set([
  "Emotions",
  "HealJai Talk",
  "Healing Horoscope V1",
  "Healing Horoscope V2",
  "Healing Horoscope V3",
  "Healing Horoscope",
  "รหัส Healjai V3",
  "Energy Match",
]);

const astriaCountryCategorySet = new Set(ASTRIA_COUNTRY_CATEGORIES);

const COLORS = {
  amethyst: "#6D4C92",
  darkAmethyst: "#2D1B44",
  roseGold: "#E5B8AD",
};

// Jp kyusei & timing tab routing
function resolveJapanDashboardPath(
  name: string | null | undefined,
): string | null {
  if (!name) return null;
  const lower = name.toLowerCase();
  if (
    lower.includes("kyusei") ||
    lower.includes("viral") ||
    lower.includes("omamori")
  ) {
    return "/astria-japan/kyusei";
  }
  if (lower.includes("timing")) {
    return "/astria-japan/timing";
  }
  return null;
}

// Astria GCC V2 — 6-tab emotional check-in dashboard routing.
function resolveGCCV2DashboardPath(
  parentCategoryName: string | null | undefined,
  subCategoryName: string | null | undefined,
): string | null {
  if (parentCategoryName !== "Astria GCC V2") return null;
  if (!subCategoryName) return null;
  const lower = subCategoryName.toLowerCase();
  if (lower.includes("timing")) return "/astria-gcc-v2/timing";
  if (lower.includes("kyusei")) return "/astria-gcc-v2/kyusei";
  if (lower.includes("companion")) return "/astria-gcc-v2/companion";
  if (lower.includes("emotional"))
    return "/astria-gcc-v2/emotional-intelligence";
  if (lower.includes("personality")) return "/astria-gcc-v2/personality-engine";
  return null;
}

// Astria Philippines V2 — 6-tab emotional check-in dashboard routing.
function resolvePhilippinesV2DashboardPath(
  parentCategoryName: string | null | undefined,
  subCategoryName: string | null | undefined,
): string | null {
  if (parentCategoryName !== "Astria Philippines V2") return null;
  if (!subCategoryName) return null;
  const lower = subCategoryName.toLowerCase();
  if (lower.includes("summary")) return "/astria-philippines-v2/soft-summary";
  if (lower.includes("connection"))
    return "/astria-philippines-v2/connection-atmosphere";
  if (lower.includes("timing") || lower.includes("rhythm"))
    return "/astria-philippines-v2/timing-rhythm";
  if (lower.includes("balance") || lower.includes("emotion"))
    return "/astria-philippines-v2/emotion-balance";
  if (lower.includes("guidance") || lower.includes("direction"))
    return "/astria-philippines-v2/guidance-path";
  if (lower.includes("note")) return "/astria-philippines-v2/your-note";
  return null;
}

// Astria Indonesia V2 — 6-tab emotional check-in dashboard routing.
function resolveIndonesiaV2DashboardPath(
  parentCategoryName: string | null | undefined,
  subCategoryName: string | null | undefined,
): string | null {
  if (parentCategoryName !== "Astria Indonesia V2") return null;
  if (!subCategoryName) return null;
  const lower = subCategoryName.toLowerCase();
  if (lower.includes("summary")) return "/astria-indonesia-v2/soft-summary";
  if (lower.includes("connection"))
    return "/astria-indonesia-v2/connection-atmosphere";
  if (lower.includes("timing") || lower.includes("rhythm"))
    return "/astria-indonesia-v2/timing-rhythm";
  if (lower.includes("balance") || lower.includes("emotion"))
    return "/astria-indonesia-v2/emotion-balance";
  if (lower.includes("guidance") || lower.includes("direction"))
    return "/astria-indonesia-v2/guidance-path";
  if (lower.includes("note")) return "/astria-indonesia-v2/your-note";
  return null;
}

// Astria Vietnam V2 — 6-tab emotional check-in dashboard routing.
function resolveVietnamV2DashboardPath(
  parentCategoryName: string | null | undefined,
  subCategoryName: string | null | undefined,
): string | null {
  if (parentCategoryName !== "Astria Vietnam V2") return null;
  if (!subCategoryName) return null;
  const lower = subCategoryName.toLowerCase();
  if (lower.includes("summary")) return "/astria-vietnam-v2/soft-summary";
  if (lower.includes("connection"))
    return "/astria-vietnam-v2/connection-atmosphere";
  if (lower.includes("timing") || lower.includes("rhythm"))
    return "/astria-vietnam-v2/timing-rhythm";
  if (lower.includes("balance") || lower.includes("emotion"))
    return "/astria-vietnam-v2/emotion-balance";
  if (lower.includes("guidance") || lower.includes("direction"))
    return "/astria-vietnam-v2/guidance-path";
  if (lower.includes("note")) return "/astria-vietnam-v2/your-note";
  return null;
}

// Astria Brazil V2 — 6-tab emotional check-in dashboard routing.
function resolveBrazilV2DashboardPath(
  parentCategoryName: string | null | undefined,
  subCategoryName: string | null | undefined,
): string | null {
  if (parentCategoryName !== "Astria Brazil V2") return null;
  if (!subCategoryName) return null;
  const lower = subCategoryName.toLowerCase();
  if (lower.includes("summary")) return "/astria-brazil-v2/soft-summary";
  if (lower.includes("connection"))
    return "/astria-brazil-v2/connection-atmosphere";
  if (lower.includes("timing") || lower.includes("rhythm"))
    return "/astria-brazil-v2/timing-rhythm";
  if (lower.includes("balance") || lower.includes("emotion"))
    return "/astria-brazil-v2/emotion-balance";
  if (lower.includes("guidance") || lower.includes("direction"))
    return "/astria-brazil-v2/guidance-path";
  if (lower.includes("note")) return "/astria-brazil-v2/your-note";
  return null;
}

// Astria Mexico V2 — 6-tab emotional check-in dashboard routing.
function resolveMexicoV2DashboardPath(
  parentCategoryName: string | null | undefined,
  subCategoryName: string | null | undefined,
): string | null {
  if (parentCategoryName !== "Astria Mexico V2") return null;
  if (!subCategoryName) return null;
  const lower = subCategoryName.toLowerCase();
  if (lower.includes("summary")) return "/astria-mexico-v2/soft-summary";
  if (lower.includes("connection"))
    return "/astria-mexico-v2/connection-atmosphere";
  if (lower.includes("timing") || lower.includes("rhythm"))
    return "/astria-mexico-v2/timing-rhythm";
  if (lower.includes("balance") || lower.includes("emotion"))
    return "/astria-mexico-v2/emotion-balance";
  if (lower.includes("guidance") || lower.includes("direction"))
    return "/astria-mexico-v2/guidance-path";
  if (lower.includes("note")) return "/astria-mexico-v2/your-note";
  return null;
}

// Astria Mexico — 7-tab dashboard routing. Strictly scoped to the "Astria
// Mexico" category so it never collides with the existing "Astria Mexico V2"
// daily check-in lane, which still routes through the generic default chat.
function resolveMexicoDashboardPath(
  parentCategoryName: string | null | undefined,
  subCategoryName: string | null | undefined,
): string | null {
  if (parentCategoryName !== "Astria Mexico") return null;
  if (!subCategoryName) return null;
  const lower = subCategoryName.toLowerCase();

  if (lower.includes("energy match") || lower.includes("energy_match"))
    return "/astria-mexico/energy-match";
  if (lower.includes("compatibility") || lower.includes("compatibilidad"))
    return "/astria-mexico/compatibility";
  if (lower.includes("cosmic message") || lower.includes("cosmic_message"))
    return "/astria-mexico/cosmic-message";
  if (lower.includes("relationship") || lower.includes("relacion"))
    return "/astria-mexico/relationship";
  if (lower.includes("companion talk") || lower.includes("companion_talk"))
    return "/astria-mexico/companion-talk";
  if (lower.includes("daily flow") || lower.includes("daily_flow"))
    return "/astria-mexico/daily-flow";
  if (
    lower.includes("zodiac personality") ||
    lower.includes("zodiac_personality")
  )
    return "/astria-mexico/zodiac-personality";

  return null;
}

// Astria India V2 — 7-tab dashboard routing. Astria India V3 reuses the
// exact same dashboard paths/components (see AstriaIndiaV2Shell's version
// toggle) rather than a separate route tree, so it's accepted here too —
// the call site appends a "&v=3" query flag when the V3 category was the
// one actually clicked (see indiaVersionQuery below).
function resolveIndiaV2DashboardPath(
  parentCategoryName: string | null | undefined,
  subCategoryName: string | null | undefined,
): string | null {
  if (
    parentCategoryName !== "Astria India V2" &&
    parentCategoryName !== "Astria India V3"
  )
    return null;
  if (!subCategoryName) return null;
  const lower = subCategoryName.toLowerCase();

  if (lower.includes("aapka") || lower.includes("note"))
    return "/astria-india-v2/aapka-note";
  if (lower.includes("samay") || lower.includes("pravah"))
    return "/astria-india-v2/samay";
  if (lower.includes("vyaktitva") || lower.includes("darshan"))
    return "/astria-india-v2/vyaktitva";
  if (lower.includes("bhavna") || lower.includes("drishti"))
    return "/astria-india-v2/bhavna";
  if (lower.includes("upay") || lower.includes("marg"))
    return "/astria-india-v2/upay";
  if (lower.includes("vivah") || lower.includes("muhurat"))
    return "/astria-india-v2/vivah";
  if (
    lower.includes("sambandh") ||
    lower.includes("taal") ||
    lower.includes("mel")
  )
    return "/astria-india-v2/sambandh";

  return null;
}

const TesterHome: React.FC = () => {
  const { t } = useTranslation() as any;
  const navigate = useNavigate();
  const { user } = useAuth();
  const { categories } = useCategories();
  const { subCategories } = useSubCategories();
  const { startNewChat, setCurrentChat } = useChat();

  const myLang = useAppLanguage();
  const userRegion = user?.region || "healjai";

  const [showSubCategoryModal, setShowSubCategoryModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [subCategoriesForModal, setSubCategoriesForModal] = useState<any[]>([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (categories.length > 0) {
      setPageLoading(false);
      return;
    }
    const timeout = setTimeout(() => setPageLoading(false), 4000);
    return () => clearTimeout(timeout);
  }, [categories]);

  const visibleCategories = useMemo(() => {
    const active = categories.filter((c) => c.isActive);

    // Healjai-region testers see everything a healjai user would (including
    // HEALJAI_ONLY_CATEGORIES), just minus the other Astria regions.
    // Mirrors the tester filtering logic in Sidebar.tsx.
    if (userRegion === "healjai") {
      return active.filter((c) => !astriaCountryCategorySet.has(c.name));
    }

    const allowedAstria = REGION_TO_ASTRIA[userRegion];

    return active.filter((c) => {
      const isAstriaCategory = astriaCountryCategorySet.has(c.name);
      const isHealjaiOnly = HEALJAI_ONLY_CATEGORIES.has(c.name);
      const isAllowedAstria = allowedAstria?.includes(c.name); // ← was c.name === allowedAstria

      if (isAllowedAstria) return true;
      if (isAstriaCategory) return false;
      if (isHealjaiOnly) return false;
      return true;
    });
  }, [categories, userRegion]);

  const applyTesterBranding = (label: string) =>
    label ? label.replace(/Astria/gi, "Cosmic") : label;

  const getCategoryLabel = (cat: any) =>
    applyTesterBranding(localizedField(cat, "name", myLang));

  const getCategoryDescription = (cat: any) =>
    localizedField(cat, "description", myLang);

  const handleCategoryClick = (categoryId: string) => {
    const category = categories.find((c) => c.id === categoryId);
    if (!category) return;

    const categoryNameLower = category.name.toLowerCase();

    // Social Compatibility bypasses the normal chat flow entirely — it's a
    // profile/match/connect module, not a chat. Mirrors Sidebar.tsx's routing
    // for the same category.
    if (categoryNameLower.includes("social compatibility")) {
      navigate(user?.hasCompatibilityProfile ? "/matches" : "/compatibility-form");
      return;
    }

    if (category.name === "Astria Indonesia V3") {
      navigate(`/indomodule?categoryId=${categoryId}`);
      return;
    }

    if (
      categoryNameLower.includes("check-mate") ||
      categoryNameLower.includes("checkmate") ||
      categoryNameLower.includes("check mate")
    ) {
      navigate("/checkmate");
      return;
    }

    if (
      categoryNameLower.includes("energymatchv3") ||
      categoryNameLower.includes("energy match v3")
    ) {
      navigate(`/energyMatchV3?categoryId=${categoryId}`);
      return;
    }

    if (
      categoryNameLower.includes("energymatchv2") ||
      categoryNameLower.includes("energy match v2")
    ) {
      navigate(`/energyMatchV2?categoryId=${categoryId}`);
      return;
    }

    if (categoryNameLower.includes("matescan")) {
      navigate(`/matescan?categoryId=${categoryId}`);
      return;
    }

    if (category.name === "Energy Match") {
      navigate(`/energyMatch?category=${categoryId}`);
      return;
    }

    // Route Kyusei Viral / Timing Flow to their standalone dashboards when
    // clicked directly as a top-level category.
    const directJapanDashboardPath = resolveJapanDashboardPath(category.name);
    if (directJapanDashboardPath) {
      navigate(`${directJapanDashboardPath}?categoryId=${categoryId}`);
      return;
    }

    const activeSubCategories = subCategories.filter(
      (sc) => sc.categoryId === categoryId && sc.isActive,
    );

    if (activeSubCategories.length > 0) {
      setSelectedCategory({ id: categoryId, name: category.name });
      setSubCategoriesForModal(
        activeSubCategories.map((sc) => ({
          id: sc.id,
          ...pickLocalizedFields(sc, ["name", "description"]),
          icon: sc.icon,
        })),
      );
      setShowSubCategoryModal(true);
      return;
    }

    startChatForCategory(categoryId);
  };

  const startChatForCategory = async (
    categoryId: string,
    subCategoryId?: string,
  ) => {
    try {
      setActionLoading(true);
      const newChat = await startNewChat(categoryId, subCategoryId);
      setCurrentChat(newChat);
      navigate(`/sanctuary?chat=${newChat.id}`);
    } catch (error) {
      console.error("Failed to start new chat:", error);
      toast.error(t("sidebar.failedToStartChat"));
      setActionLoading(false);
    }
  };

  const handleSubCategorySelect = async (
    subCategoryId: string,
    categoryName: string,
  ) => {
    if (!selectedCategory) return;

    const isCountryModule = ASTRIA_COUNTRY_CATEGORIES.includes(
      selectedCategory.name,
    );
    const subCategoryLower = categoryName.toLowerCase();

    if (
      subCategoryLower.includes("marriage verdict") ||
      subCategoryLower.includes("marriage-verdict")
    ) {
      setActionLoading(true);
      navigate("/marriage-verdict");
      handleSubCategoryModalClose();
      return;
    }

    if (
      subCategoryLower.includes("energymatchv2") ||
      subCategoryLower.includes("energy match v2")
    ) {
      setActionLoading(true);
      navigate(
        `/energyMatchV2?categoryId=${selectedCategory.id}&subCategoryId=${subCategoryId}`,
      );
      handleSubCategoryModalClose();
      return;
    }

    // Route the Saju subcategory (under Astria Korea or Astria Korea V3) to
    // the standalone Saju form — separate from the normal chat thread,
    // structured JSON response.
    if (
      (selectedCategory.name === "Astria Korea" ||
        selectedCategory.name === "Astria Korea V3") &&
      subCategoryLower.includes("saju")
    ) {
      setActionLoading(true);
      navigate(`/saju?categoryId=${subCategoryId}`);
      handleSubCategoryModalClose();
      return;
    }

    // GCC V2's dashboard is checked first (strictly scoped to "Astria GCC
    // V2") so its subcategory names never fall through into the Japan
    // resolver below, which matches by name-substring alone.
    const gccV2DashboardPath = resolveGCCV2DashboardPath(
      selectedCategory.name,
      categoryName,
    );
    if (gccV2DashboardPath) {
      setActionLoading(true);
      navigate(`${gccV2DashboardPath}?categoryId=${subCategoryId}`);
      handleSubCategoryModalClose();
      return;
    }

    const indiaV2DashboardPath = resolveIndiaV2DashboardPath(
      selectedCategory.name,
      categoryName,
    );
    if (indiaV2DashboardPath) {
      // Astria India V3 reuses V2's dashboard paths — the "&v=3" flag is
      // how the shared tab components know to target the V3 category/
      // engine instead of defaulting to V2.
      const indiaVersionQuery =
        selectedCategory.name === "Astria India V3" ? "&v=3" : "";
      setActionLoading(true);
      navigate(
        `${indiaV2DashboardPath}?categoryId=${subCategoryId}${indiaVersionQuery}`,
      );
      handleSubCategoryModalClose();
      return;
    }

    const philippinesV2DashboardPath = resolvePhilippinesV2DashboardPath(
      selectedCategory.name,
      categoryName,
    );
    if (philippinesV2DashboardPath) {
      setActionLoading(true);
      navigate(`${philippinesV2DashboardPath}?categoryId=${subCategoryId}`);
      handleSubCategoryModalClose();
      return;
    }

    const indonesiaV2DashboardPath = resolveIndonesiaV2DashboardPath(
      selectedCategory.name,
      categoryName,
    );
    if (indonesiaV2DashboardPath) {
      setActionLoading(true);
      navigate(`${indonesiaV2DashboardPath}?categoryId=${subCategoryId}`);
      handleSubCategoryModalClose();
      return;
    }

    const vietnamV2DashboardPath = resolveVietnamV2DashboardPath(
      selectedCategory.name,
      categoryName,
    );
    if (vietnamV2DashboardPath) {
      setActionLoading(true);
      navigate(`${vietnamV2DashboardPath}?categoryId=${subCategoryId}`);
      handleSubCategoryModalClose();
      return;
    }

    const brazilV2DashboardPath = resolveBrazilV2DashboardPath(
      selectedCategory.name,
      categoryName,
    );
    if (brazilV2DashboardPath) {
      setActionLoading(true);
      navigate(`${brazilV2DashboardPath}?categoryId=${subCategoryId}`);
      handleSubCategoryModalClose();
      return;
    }

    const mexicoV2DashboardPath = resolveMexicoV2DashboardPath(
      selectedCategory.name,
      categoryName,
    );
    if (mexicoV2DashboardPath) {
      setActionLoading(true);
      navigate(`${mexicoV2DashboardPath}?categoryId=${subCategoryId}`);
      handleSubCategoryModalClose();
      return;
    }

    const mexicoDashboardPath = resolveMexicoDashboardPath(
      selectedCategory.name,
      categoryName,
    );
    if (mexicoDashboardPath) {
      setActionLoading(true);
      navigate(`${mexicoDashboardPath}?categoryId=${subCategoryId}`);
      handleSubCategoryModalClose();
      return;
    }

    const japanDashboardPath = resolveJapanDashboardPath(categoryName);
    if (japanDashboardPath) {
      setActionLoading(true);
      navigate(`${japanDashboardPath}?categoryId=${subCategoryId}`);
      handleSubCategoryModalClose();
      return;
    }

    const isCompatibilitySubcategory =
      subCategoryLower.includes("compatibility") ||
      subCategoryLower.includes("energy match") ||
      subCategoryLower.includes("energymatch");
    const isDedicatedCompatibilityRegion =
      (selectedCategory.name === "Astria Japan" ||
        selectedCategory.name === "Astria Japan V3" ||
        selectedCategory.name === "Astria Korea" ||
        selectedCategory.name === "Astria Korea V2" ||
        selectedCategory.name === "Astria Korea V3" ||
        selectedCategory.name === "Astria GCC" ||
        selectedCategory.name === "Astria Indonesia") &&
      subCategoryLower.includes("compatibility");

    if (
      isCountryModule &&
      isCompatibilitySubcategory &&
      !isDedicatedCompatibilityRegion
    ) {
      setActionLoading(true);
      navigate(
        `/energyMatch?country=${encodeURIComponent(selectedCategory.name)}&categoryId=${selectedCategory.id}&subCategoryId=${subCategoryId}`,
      );
      handleSubCategoryModalClose();
      return;
    }

    if (selectedCategory.name === "Tarot") {
      setActionLoading(true);
      navigate(`/tarotCard?category=69dced23b631dcb2c1be3008`);
      handleSubCategoryModalClose();
      return;
    }

    if (selectedCategory.name === "LifeGraph") {
      setActionLoading(true);
      navigate(`/lifeGraph?category=${subCategoryId}`);
      handleSubCategoryModalClose();
      return;
    }

    await startChatForCategory(selectedCategory.id, subCategoryId);
    handleSubCategoryModalClose();
  };

  const handleSubCategoryModalClose = () => {
    setShowSubCategoryModal(false);
    setSelectedCategory(null);
    setSubCategoriesForModal([]);
  };

  if (pageLoading) {
    return (
      <div className="min-h-screen w-full bg-[#f0f2f1] flex flex-col items-center justify-center gap-4">
        <div
          className="w-12 h-12 rounded-full border-4 animate-spin"
          style={{
            borderColor: `${COLORS.amethyst}33`,
            borderTopColor: COLORS.amethyst,
          }}
        />
        <p
          className="text-sm font-medium"
          style={{ color: COLORS.darkAmethyst }}
        >
          {t("Sidebar Loading...") || "Loading..."}
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-[#f0f2f1] flex flex-col items-center px-4 sm:px-6 lg:px-10 py-10 relative">
      {actionLoading && (
        <div className="fixed inset-0 z-[60] flex flex-col items-center justify-center gap-4 bg-white/70 backdrop-blur-sm">
          <div
            className="w-12 h-12 rounded-full border-4 animate-spin"
            style={{
              borderColor: `${COLORS.amethyst}33`,
              borderTopColor: COLORS.amethyst,
            }}
          />
          <p
            className="text-sm font-medium"
            style={{ color: COLORS.darkAmethyst }}
          >
            {t("Sidebar Loading...") || "Loading..."}
          </p>
        </div>
      )}

      <SubCategorySelectionModal
        isOpen={showSubCategoryModal}
        onClose={handleSubCategoryModalClose}
        onSelect={handleSubCategorySelect}
        categoryName={applyTesterBranding(selectedCategory?.name || "")}
        subCategories={subCategoriesForModal}
      />

      <div className="w-full max-w-[1100px] mx-auto flex flex-col items-center">
        <h1
          className="text-2xl sm:text-3xl font-bold mb-8 text-center"
          style={{ color: COLORS.darkAmethyst }}
        >
          {t("sidebar.selectCategory") || "Select a Category"}
        </h1>

        <div className="flex flex-wrap justify-center gap-6 w-full">
          {visibleCategories.map((cat) => {
            const Icon = getCategoryIcon(cat.icon);
            return (
              <button
                key={cat.id}
                onClick={() => handleCategoryClick(cat.id)}
                disabled={actionLoading}
                className="w-full sm:w-[calc(50%-12px)] lg:w-[300px] max-w-[320px] text-left rounded-[26px] px-6 py-7 bg-white border border-[#E8E3DA]
          shadow-[0_8px_25px_rgba(0,0,0,0.06)] transition-all duration-300
          hover:-translate-y-1 hover:shadow-[0_15px_35px_rgba(0,0,0,0.10)]
          disabled:opacity-60 disabled:cursor-not-allowed disabled:translate-y-0"
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                  style={{ backgroundColor: `${COLORS.amethyst}15` }}
                >
                  <Icon size={22} style={{ color: COLORS.amethyst }} />
                </div>
                <h3
                  className="font-semibold text-base mb-2"
                  style={{ color: COLORS.darkAmethyst }}
                >
                  {getCategoryLabel(cat)}
                </h3>
                {getCategoryDescription(cat) && (
                  <p className="text-sm text-[#4A4A4A] line-clamp-2">
                    {getCategoryDescription(cat)}
                  </p>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TesterHome;
